'use client';

import { auth } from '@/utils/firebase-client';
import {
  linkWithCredential,
  PhoneAuthProvider,
  RecaptchaVerifier,
  signInWithCredential,
  signOut,
} from 'firebase/auth';

const RECAPTCHA_CONTAINER_ID = 'firebase-recaptcha-container';
const ALREADY_RENDERED_RE = /already been rendered/i;

let recaptchaVerifier: RecaptchaVerifier | null = null;
let sendInFlight: { phoneNumber: string; promise: Promise<string | null> } | null =
  null;

const getOrCreateRecaptchaContainer = (): HTMLElement => {
  let container = document.getElementById(RECAPTCHA_CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = RECAPTCHA_CONTAINER_ID;
    // Keep this node out of React's tree. Invisible reCAPTCHA injects
    // children; a JSX container would have those nodes removed on re-render,
    // after which grecaptcha still considers the element rendered.
    container.setAttribute('aria-hidden', 'true');
    container.style.position = 'absolute';
    container.style.width = '1px';
    container.style.height = '1px';
    container.style.left = '-9999px';
    container.style.overflow = 'hidden';
    document.body.appendChild(container);
  }
  return container;
};

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

/**
 * Returns a singleton RecaptchaVerifier. Creating a second instance against
 * the same element throws "reCAPTCHA has already been rendered in this element".
 * RecaptchaVerifier.clear() also does not unmount invisible widgets, so callers
 * must reuse this instance across sends.
 */
export const initializeRecaptchaVerifier = (): RecaptchaVerifier => {
  if (recaptchaVerifier) {
    return recaptchaVerifier;
  }

  recaptchaVerifier = new RecaptchaVerifier(auth, getOrCreateRecaptchaContainer(), {
    size: 'invisible',
    callback: () => {
      // reCAPTCHA solved
    },
    'expired-callback': () => {
      // Token expired; the next verify() re-executes the same widget
    },
  });
  return recaptchaVerifier;
};

/**
 * Destroys the verifier and removes its container so a later init can render
 * into a fresh element. Invisible widgets are not removed by clear() alone.
 */
export const clearRecaptchaVerifier = (): void => {
  if (recaptchaVerifier) {
    try {
      recaptchaVerifier.clear();
    } catch {
      // Already destroyed
    }
    recaptchaVerifier = null;
  }

  document.getElementById(RECAPTCHA_CONTAINER_ID)?.remove();
};

const verifyPhoneNumber = async (phoneNumber: string): Promise<string> => {
  const phoneProvider = new PhoneAuthProvider(auth);
  return phoneProvider.verifyPhoneNumber(
    phoneNumber,
    initializeRecaptchaVerifier()
  );
};

const sendPhoneVerificationCodeOnce = async (
  phoneNumber: string
): Promise<string | null> => {
  try {
    return await verifyPhoneNumber(phoneNumber);
  } catch (error: unknown) {
    if (ALREADY_RENDERED_RE.test(getErrorMessage(error))) {
      clearRecaptchaVerifier();
      try {
        return await verifyPhoneNumber(phoneNumber);
      } catch (retryError) {
        console.error('Error sending phone verification code:', retryError);
        clearRecaptchaVerifier();
        // Surface rate-limit errors so the UI can show a clear message
        const retryCode =
          typeof retryError === 'object' &&
          retryError !== null &&
          'code' in retryError
            ? String((retryError as { code: unknown }).code)
            : '';
        if (retryCode === 'auth/too-many-requests') {
          throw retryError;
        }
        return null;
      }
    }

    console.error('Error sending phone verification code:', error);
    const code =
      typeof error === 'object' && error !== null && 'code' in error
        ? String((error as { code: unknown }).code)
        : '';
    if (code === 'auth/too-many-requests') {
      throw error;
    }
    return null;
  }
};

/**
 * Send SMS verification code to phone number
 * @param phoneNumber - E.164 format phone number (e.g., +15551234567)
 * @returns verificationId - Used to verify the SMS code
 */
export const sendPhoneVerificationCode = async (
  phoneNumber: string
): Promise<string | null> => {
  if (sendInFlight) {
    if (sendInFlight.phoneNumber === phoneNumber) {
      return sendInFlight.promise;
    }
    await sendInFlight.promise;
  }

  const promise = sendPhoneVerificationCodeOnce(phoneNumber).finally(() => {
    if (sendInFlight?.promise === promise) {
      sendInFlight = null;
    }
  });
  sendInFlight = { phoneNumber, promise };
  return promise;
};

/**
 * Verify SMS code and sign in with phone credential
 * @param verificationId - ID from sendPhoneVerificationCode
 * @param verificationCode - 6-digit SMS code
 * @returns boolean - true if successful
 */
export const verifyPhoneCode = async (
  verificationId: string,
  verificationCode: string
): Promise<boolean> => {
  try {
    const credential = PhoneAuthProvider.credential(
      verificationId,
      verificationCode
    );

    const currentUser = auth.currentUser;

    if (currentUser) {
      // Try to link phone credential to existing user
      try {
        await linkWithCredential(currentUser, credential);
        return true;
      } catch (linkError: any) {
        console.log('Link error:', linkError.code);

        // If phone already linked to current user, just sign in
        if (linkError.code === 'auth/provider-already-linked') {
          await signInWithCredential(auth, credential);
          return true;
        }

        // If phone belongs to a different Firebase account, this indicates
        // the user should have been signed out before verification
        if (
          linkError.code === 'auth/account-exists-with-different-credential'
        ) {
          console.warn(
            'Phone belongs to different account - attempting to recover by switching accounts'
          );
          // Attempt recovery: sign out and sign in with new credential
          await signOut(auth);
          await signInWithCredential(auth, credential);
          return true;
        }

        throw linkError;
      }
    } else {
      await signInWithCredential(auth, credential);
      return true;
    }
  } catch (error) {
    console.error('Error verifying phone code:', error);
    return false;
  }
};
